import { IonButton } from '@ionic/react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDeliveryStatusByMark } from '../../api/hooks/delivery/useDeliveryStatusByMark';
import { Loading } from '../../components/atoms/Loading';
import { STANDALONE_FEATURE } from '../../interfaces/enumerables';
import { canRegisterRejection } from '../../services/utils/deliveryStatusUtils';
import { useHasActiveFeature } from '../../services/utils/useHasActiveFeature';
import { DeliveryInstructionsLink } from './component/DeliveryInstructionsLink';
import { DeliveryRejectDeliveryForm } from './component/DeliveryRejectDeliveryForm';
import { DeliveryRetrieveDocByMark } from './component/DeliveryRetrieveDocByMark';
import { DeliveryStatusGsisDoc } from './component/DeliveryStatusGsisDoc';
import { DeliveryStatusSteps } from './component/DeliveryStatusSteps';
import { ProviderInvoiceDoc } from './component/ProviderInvoiceDoc';

export const NewRejectDeliveryScreen = () => {
  const { isChecking: isFeatureChecking } = useHasActiveFeature(STANDALONE_FEATURE.TRANSIT, true);
  const { t } = useTranslation();
  const { mark } = useParams<{ mark: string }>();
  const isCreatePath = useMemo(() => mark === 'create', [mark]);

  const [searchMark, setSearchMark] = useState<string | undefined>(undefined);
  const [formKey, setFormKey] = useState(0);

  const effectiveMark = useMemo(() => (isCreatePath ? searchMark : mark), [isCreatePath, mark, searchMark]);

  const { data, isLoading, isRefetching } = useDeliveryStatusByMark(effectiveMark);

  const hasDoc = !!data?.extraData?.doc;
  const showRetrieveForm = isCreatePath && !hasDoc;

  const handleSearch = useCallback((m: string) => setSearchMark(m), []);

  const handleReset = useCallback(() => {
    setSearchMark(undefined);
    setFormKey((k) => k + 1);
  }, []);

  const getLoading = useCallback(() => {
    if (isLoading || isRefetching || isFeatureChecking) {
      return <Loading backgroundFull />;
    }

    return null;
  }, [isLoading, isRefetching, isFeatureChecking]);

  const getErrorCode = useCallback(() => {
    if (data?.errorCode && !data?.extraData?.doc) {
      return (
        <div className="m-t-16 m-b-16 flex-center-center-column-nowrap width-100">
          <div className="boldText4 text-center text-danger text-with-new-line max-width-900">
            {t(`API_ERRORS.${data?.errorCode}`)}
          </div>
        </div>
      );
    }
    return null;
  }, [data?.errorCode, data?.extraData?.doc, t]);

  const getCurrentStatus = useCallback(() => {
    if (data?.data) {
      return (
        <div className="margin-b-16 flex-center-center-column-nowrap width-100">
          <div className="boldText6">{t('DELIVERY.DELIVERY_STATUS')}</div>
          <div className="margin-t-16 simpleBorder p-12">
            <div className="normalText5 m-b-6">
              <b>{t('DELIVERY.NEW_DELIVERY_MARK_PLACEHOLDER')}</b>: {data?.data?.invoiceMark}
            </div>
            <div className="normalText5">
              <b>{t('DELIVERY.DELIVERY_STATUS_CURRENT')}</b>: {t(`DELIVERY.STATUS.${data?.data?.status}`)}
            </div>
          </div>
        </div>
      );
    }
  }, [data?.data, t]);

  const getGsisDocData = useCallback(() => {
    if (data?.extraData?.doc) {
      return (
        <div className="m-b-32 flex-center-center-column-nowrap width-100">
          <DeliveryStatusGsisDoc doc={data?.extraData?.doc} />
        </div>
      );
    }
    return null;
  }, [data?.extraData?.doc]);

  const getProviderInvoiceData = useCallback(() => {
    const qrUrl = data?.extraData?.doc?.qrCodeUrl;
    if (!qrUrl) return null;
    return (
      <div className="m-b-32 flex-center-center-column-nowrap width-100">
        <ProviderInvoiceDoc qrUrl={qrUrl} />
      </div>
    );
  }, [data?.extraData?.doc?.qrCodeUrl]);

  const getSteps = useCallback(() => {
    if (data?.data?.lifecycleHistory) {
      return (
        <div className="m-b-32 flex-center-center-column-nowrap width-100">
          <DeliveryStatusSteps lifecycleHistory={data?.data?.lifecycleHistory} />
        </div>
      );
    }
  }, [data?.data?.lifecycleHistory]);

  const getRejectForm = useCallback(() => {
    if (!isLoading && canRegisterRejection(data?.extraData?.isIssuer, data?.data, data?.extraData?.doc)) {
      return (
        <div className="m-b-32 flex-center-center-column-nowrap width-100">
          <DeliveryRejectDeliveryForm key={formKey} mark={effectiveMark} qrUrl={data?.extraData?.doc?.qrCodeUrl} />
        </div>
      );
    }
  }, [data?.data, data?.extraData?.doc, data?.extraData?.isIssuer, effectiveMark, formKey, isLoading]);

  const getResetButton = useCallback(() => {
    if (!isCreatePath || hasDoc || searchMark) {
      return (
        <div className="m-b-16 flex-center-center-column-nowrap width-100">
          <IonButton fill="outline" mode="md" onClick={handleReset}>
            {t('DELIVERY.RESET_SEARCH')}
          </IonButton>
        </div>
      );
    }
  }, [handleReset, hasDoc, isCreatePath, searchMark, t]);

  return (
    <div className="scrollable">
      {getLoading()}
      <div className="width-100 p-t-16 p-b-16 flex-center-start-column-nowrap p-r-24 p-l-24">
        <DeliveryInstructionsLink />
        {getErrorCode()}
        {getCurrentStatus()}
        {getResetButton()}
        {showRetrieveForm && (
          <div className="m-b-32 flex-center-center-column-nowrap width-100">
            <DeliveryRetrieveDocByMark key={formKey} onSearch={handleSearch} />
          </div>
        )}
        {getRejectForm()}
        {getSteps()}
        {getGsisDocData()}
        {getProviderInvoiceData()}
      </div>
    </div>
  );
};
